import { PasseriniAPI } from './apiService';

export interface SystemConfiguration {
  default_filters: DefaultFiltersConfig;
  subtramites_configuration: SubtramitesConfig;
  file_settings: FileSettingsConfig;
  session_settings: SessionSettingsConfig;
  notification_settings: NotificationSettingsConfig;
  interface_settings: InterfaceSettingsConfig;
  whatsapp_settings: WhatsAppSettingsConfig;
  contabilidad_settings: ContabilidadSettingsConfig;
  presupuestos_settings: PresupuestosSettingsConfig;
}

export interface DefaultFiltersConfig {
  tramite_status: string[];
  date_range_days: number;
  sort_order: string;
}

export interface SubtramitesConfig {
  default_enabled: string[];
}

export interface FileSettingsConfig {
  max_file_size_mb: number;
  allowed_extensions: string[];
  auto_backup_enabled: boolean;
}

export interface SessionSettingsConfig {
  timeout_minutes: number;
  auto_save_interval_seconds: number;
}

export interface NotificationSettingsConfig {
  email_enabled: boolean;
  reminder_frequency_days: number;
  auto_notifications: string[];
}

export interface InterfaceSettingsConfig {
  default_theme: "light" | "dark";
  items_per_page: number;
  show_advanced_filters: boolean;
}

export interface ContabilidadSettingsConfig {
  /**
   * Fecha de corte (YYYY-MM-DD). Los cobros y egresos pendientes anteriores a
   * esta fecha no se listan ni se suman en los KPIs. null = sin corte.
   */
  pendientes_desde: string | null;
  /**
   * Tipos de otro trámite cuyos cobros van al fondo de certificaciones y
   * poderes; el resto va a escrituras. Aplica a los cobros que se registren
   * desde que se cambia.
   */
  tipos_certificaciones_poderes: string[];
}

export interface PresupuestosSettingsConfig {
  /**
   * Leyendas predeterminadas del pie del PDF. Cada presupuesto nuevo recibe una
   * copia; cambiarlas no afecta a los presupuestos existentes.
   */
  notas: string[];
}

export interface WhatsAppSettingsConfig {
  active_provider: "ultramsg";
  max_recipients_per_dispatch: number;
  idempotency_window_minutes: number;
  duplicate_check_enabled: boolean;
  temp_link_expires_hours: number; // 0 = sin expiración
  temp_link_max_downloads: number | null;
  dispatch_delay_min_seconds: number;
  dispatch_delay_max_seconds: number;
}

export const systemConfigurationService = {
  // Obtener toda la configuración del sistema
  async getAll(): Promise<SystemConfiguration> {
    const response = await PasseriniAPI.get('/system-configuration');
    return response.data?.data;
  },

  // Actualizar toda la configuración del sistema
  async updateAll(config: SystemConfiguration): Promise<SystemConfiguration> {
    const response = await PasseriniAPI.put('/system-configuration', config);
    return response.data?.data;
  },

  // Helper para actualizar una sección específica
  async updateSection(section: keyof SystemConfiguration, data: any): Promise<SystemConfiguration> {
    const currentConfig = await this.getAll();
    const updatedConfig = {
      ...currentConfig,
      [section]: data
    };
    return this.updateAll(updatedConfig);
  },

  // Configuración por defecto del sistema
  getDefaultConfiguration(): SystemConfiguration {
    return {
      default_filters: {
        tramite_status: ['en_progreso', 'pendiente'],
        date_range_days: 30,
        sort_order: 'created_at_desc',
      },
      subtramites_configuration: {
        default_enabled: []
      },
      file_settings: {
        max_file_size_mb: 50,
        allowed_extensions: ['.pdf', '.doc', '.docx', '.jpg', '.png', '.jpeg', '.txt'],
        auto_backup_enabled: true,
      },
      session_settings: {
        timeout_minutes: 120,
        auto_save_interval_seconds: 30,
      },
      notification_settings: {
        email_enabled: true,
        reminder_frequency_days: 7,
        auto_notifications: ['tramite_created', 'status_changed'],
      },
      interface_settings: {
        default_theme: 'light',
        items_per_page: 25,
        show_advanced_filters: false,
      },
      whatsapp_settings: {
        active_provider: 'ultramsg',
        max_recipients_per_dispatch: 200,
        idempotency_window_minutes: 10,
        duplicate_check_enabled: true,
        temp_link_expires_hours: 48,
        temp_link_max_downloads: null,
        dispatch_delay_min_seconds: 5,
        dispatch_delay_max_seconds: 10,
      },
      contabilidad_settings: {
        pendientes_desde: '2026-09-01',
        tipos_certificaciones_poderes: [
          'Certificación de firma',
          'Certificación de copia',
          'Poder general',
          'Poder especial',
        ],
      },
      // Mismos valores iniciales que backend/config/presupuestos.php (notas).
      presupuestos_settings: {
        notas: [
          'El presente presupuesto quedará sujeto a variaciones, las que dependerán de las distintas entidades públicas, ley de honorarios, ley impositiva, código fiscal, entre otros; quedando los montos DEFINITIVOS al momento de la firma de la escritura traslativa de dominio.',
          'Se deja constancia que deberán estar al día todos los impuestos y expensas (en caso de corresponder) al momento de otorgarse la escritura traslativa de dominio.',
          'En caso de ser el precio en dólares, el mismo queda sujeto a variación según dólar BNA VENDEDOR al día anterior a la firma.',
        ],
      },
    };
  },

  // Helper para obtener solo la configuración de subtramites
  async getSubtramitesConfiguration(): Promise<SubtramitesConfig> {
    const config = await this.getAll();
    return config.subtramites_configuration;
  },

  // Helper para actualizar solo la configuración de subtramites
  async updateSubtramitesConfiguration(subtramitesConfig: SubtramitesConfig): Promise<SystemConfiguration> {
    return this.updateSection('subtramites_configuration', subtramitesConfig);
  }
};

export default systemConfigurationService;
